import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionGroup,
  AccordionTitle,
  Badge,
  Button,
  Coin,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Icon,
  InlineStack,
  Paragraph,
  Stack,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { capitalize } from '@/utils/string';
import { RecentActivity } from '../item/recent-activity';
import type { UsageScope } from '../utils/usage-surfaces';
import type { DataIndexItemAddress } from '../urls';
import { getLabelForItem } from '../utils/item';
import type { DataIndexUsage } from '../utils/types';
import { usageTarget, type UsageRecord } from '../item/usage-links';
import { UsageItem } from './usage-item';
import {
  couldBeUsedIn,
  currentlyUsedIn,
  usageSurfaces,
  USAGE_PAGE_SIZE,
  type UsageSurfaceTerms,
} from './usage-summary';

/**
 * What the drawer shows below the item's details: where it is used, and what
 * has happened to it lately.
 *
 * The usage side is accordions rather than the item page's table, because
 * the question a drawer answers is "how much of this is there?" — the
 * counts are the answer, and opening one is the follow-up.
 */

type UsageAndActivityProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  usage: DataIndexUsage;
  scope: UsageScope;
  terms: UsageSurfaceTerms;
};

export function UsageAndActivity({ workspaceId, address, usage, scope, terms }: UsageAndActivityProps) {
  const [tab, setTab] = useState('usage');
  const surfaces = usageSurfaces(usage, scope, terms);
  const usedIn = currentlyUsedIn(surfaces);
  const noun = getLabelForItem(address.kind);

  return (
    <Tabs activeTab={tab} onChange={(next) => setTab(next ?? 'usage')}>
      <TabsList>
        <TabsTab value="usage">Usage</TabsTab>
        <TabsTab value="activity">Activity</TabsTab>
      </TabsList>

      <TabsPanel value="usage">
        <Stack gap="200" pt="200">
          {usedIn == null ? (
            <EmptyState>
              <EmptyStateTitle>{capitalize(noun)} not in use</EmptyStateTitle>
              <EmptyStateDescription>
                {capitalize(plur(noun, 2))} can be used in {couldBeUsedIn(surfaces)}.
              </EmptyStateDescription>
            </EmptyState>
          ) : (
            <>
              <Paragraph>Currently used in {usedIn}</Paragraph>
              <AccordionGroup>
                {surfaces.map((surface) => (
                  <UsageAccordion
                    key={surface.id}
                    workspaceId={workspaceId}
                    label={surface.label}
                    icon={surface.icon}
                    records={usage[surface.id] ?? []}
                  />
                ))}
              </AccordionGroup>
            </>
          )}
        </Stack>
      </TabsPanel>

      <TabsPanel value="activity">
        <RecentActivity workspaceId={workspaceId} address={address} withNewestExpanded={false} />
      </TabsPanel>
    </Tabs>
  );
}

type UsageAccordionProps = {
  workspaceId: number;
  label: string;
  icon: 'segment' | 'campaigns' | 'email' | 'newsletter';
  records: readonly UsageRecord[];
};

/**
 * One surface's rows, five at a time.
 *
 * A surface with nothing on it renders nothing. The page this replaces gates
 * the accordion inside the accordion itself — `{{#if (gt @count 0)}}` at
 * `data-index-details-drawer/usage-and-activity/index.gts:640` — so its
 * drawer shows only the surfaces an item is actually on, however many the
 * plan offers. Where the surfaces an item *could* be on are the point, the
 * empty state says so in a sentence instead. The item page's views are the
 * other way round and keep their zeros, which is also Ember's doing.
 */
function UsageAccordion({ workspaceId, label, icon, records }: UsageAccordionProps) {
  const [shown, setShown] = useState(USAGE_PAGE_SIZE);

  if (records.length === 0) {
    return null;
  }

  return (
    <Accordion variant="bordered">
      <AccordionTitle>
        <InlineStack alignBlock="center" gap="100">
          <Coin size="sm">
            <Icon name={icon} />
          </Coin>
          <Text>{label}</Text>
          <Badge>{records.length}</Badge>
        </InlineStack>
      </AccordionTitle>
      <AccordionContent>
        <Stack gap="100">
          {records.slice(0, shown).map((record) => {
            const target = usageTarget(workspaceId, record);
            return <UsageItem key={target.id} record={record} href={target.href} />;
          })}
          {records.length > shown ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                icon="add"
                iconPosition="leading"
                onClick={() => setShown((current) => current + USAGE_PAGE_SIZE)}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </Stack>
      </AccordionContent>
    </Accordion>
  );
}
